import { useEffect, useState } from "react";

interface DayCalendarProps {
  date: string;
  onDateChange: (date: string) => void;
}

function localDate(date: string): Date {
  const [year, month, day] = date.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function dateISO(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function DayCalendar({ date, onDateChange }: DayCalendarProps) {
  const [month, setMonth] = useState(() => date.slice(0, 7));
  useEffect(() => setMonth(date.slice(0, 7)), [date]);

  const today = dateISO(new Date());
  const first = localDate(`${month}-01`);
  const year = first.getFullYear();
  const monthIndex = first.getMonth();
  const days = new Date(year, monthIndex + 1, 0).getDate();
  const weeks = Math.ceil((first.getDay() + days) / 7);
  const monthLabel = first.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  function changeMonth(offset: number) {
    setMonth(dateISO(new Date(year, monthIndex + offset, 1)).slice(0, 7));
  }

  return (
    <section className="day-calendar" aria-label="Choose a day">
      <div className="day-calendar-header">
        <button type="button" aria-label="Previous month" onClick={() => changeMonth(-1)}>
          <span aria-hidden="true">‹</span>
        </button>
        <span className="day-calendar-month" aria-live="polite" aria-atomic="true">
          {monthLabel}
        </span>
        <button
          type="button"
          aria-label="Next month"
          disabled={month >= today.slice(0, 7)}
          onClick={() => changeMonth(1)}
        >
          <span aria-hidden="true">›</span>
        </button>
      </div>
      <table aria-label={monthLabel}>
        <thead>
          <tr>
            {weekdays.map((day) => (
              <th key={day} scope="col" abbr={day}>
                {day.slice(0, 2)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: weeks }, (_, week) => (
            <tr key={week}>
              {weekdays.map((weekday, column) => {
                const day = week * 7 + column - first.getDay() + 1;
                if (day < 1 || day > days) return <td key={weekday} />;
                const value = dateISO(new Date(year, monthIndex, day));
                return (
                  <td key={weekday}>
                    <button
                      type="button"
                      aria-label={new Date(year, monthIndex, day).toLocaleDateString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                      aria-pressed={date === value}
                      aria-current={today === value ? "date" : undefined}
                      disabled={value > today}
                      onClick={() => onDateChange(value)}
                    >
                      {day}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <button
        type="button"
        className="day-calendar-today"
        onClick={() => {
          const currentDay = dateISO(new Date());
          setMonth(currentDay.slice(0, 7));
          onDateChange(currentDay);
        }}
      >
        Today
      </button>
    </section>
  );
}
